import { useEffect, useMemo, useRef, useState } from 'react';
import { serverNow } from '../utils/serverClock';

/**
 * ONE clock for a whole page of lots.
 *
 * Why this exists: a 138-lot grid must not run 138 timers. The existing
 * components/auction/CountdownTimer.tsx creates its own setInterval per instance
 * — fine for the single clock on LotDetail, ruinous on a grid — so tiles use this
 * hook plus the PURE <LotClock> component instead. CountdownTimer is deliberately
 * left alone and unused by tiles.
 *
 * Returns `nowMs`, which every lot's state derives from. One interval per page,
 * one re-render per tick, all tiles consistent with each other by construction
 * (they cannot disagree about "now" because they share the same number).
 *
 * ADAPTIVE CADENCE. Ticking every second all evening is waste when the first lot
 * closes in six days. The hook runs at SLOW_MS until something is actually near,
 * then switches to FAST_MS. The switch is driven by a BOOLEAN (`fast`), not by the
 * remaining time itself — so the effect re-subscribes only when the boolean flips,
 * not on every tick.
 *
 * FAST_WINDOW_MS is deliberately wider than the UI's "closing now" threshold: in
 * slow mode we can overshoot by up to SLOW_MS, so the window has to be comfortably
 * larger than that overshoot or we would arrive late to our own countdown.
 */

const FAST_MS = 1000;
const SLOW_MS = 15000;
/** Switch to per-second ticking once anything is within this of closing. */
const FAST_WINDOW_MS = 6 * 60 * 1000;

export function useAuctionClock(soonestEndMs: number | null): number {
  const [nowMs, setNowMs] = useState<number>(() => serverNow());

  // Rarely-flipping boolean => the interval below is torn down and rebuilt at most
  // a couple of times per sale, instead of once per second.
  const fast = useMemo(() => {
    if (soonestEndMs === null) return false;
    return soonestEndMs - nowMs <= FAST_WINDOW_MS;
  }, [soonestEndMs, nowMs]);

  const period = fast ? FAST_MS : SLOW_MS;
  const periodRef = useRef(period);
  periodRef.current = period;

  useEffect(() => {
    // Tick immediately on a cadence change so the first fast frame isn't a second late.
    setNowMs(serverNow());
    const id = setInterval(() => setNowMs(serverNow()), period);
    return () => clearInterval(id);
  }, [period]);

  return nowMs;
}

export default useAuctionClock;
